// src/hooks/usePipelines.js - Hook for accessing pipeline data from PipelineStore
import usePipelineStore from '../stores/PipelineStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing pipeline data from PipelineStore
 *
 * Follows the same pattern as useEvents hook
 * Store initialization is handled at app level in SocketContext
 *
 * Uses proper Zustand selectors instead of destructuring entire store
 *
 * @returns {object} Pipeline data and operations
 */
export const usePipelines = () => {
  const socket = useSocket();

  // 🚨 Use individual selectors instead of destructuring entire store

  // Main pipelines data
  const pipelines = usePipelineStore(state => state.pipelines);
  const pipelinesLoading = usePipelineStore(state => state.pipelinesLoading);
  const pipelinesError = usePipelineStore(state => state.pipelinesError);

  // Selected pipeline data
  const selectedPipeline = usePipelineStore(state => state.selectedPipeline);
  const selectedPipelineLoading = usePipelineStore(state => state.selectedPipelineLoading);
  const selectedPipelineError = usePipelineStore(state => state.selectedPipelineError);

  // Stages data
  const stages = usePipelineStore(state => state.stages);
  const stagesLoading = usePipelineStore(state => state.stagesLoading);
  const stagesError = usePipelineStore(state => state.stagesError);

  // Rules data
  const rules = usePipelineStore(state => state.rules);
  const rulesLoading = usePipelineStore(state => state.rulesLoading);
  const rulesError = usePipelineStore(state => state.rulesError);

  // Kanban board data
  const pipelineBoard = usePipelineStore(state => state.pipelineBoard);
  const boardLoading = usePipelineStore(state => state.boardLoading);
  const boardError = usePipelineStore(state => state.boardError);

  // Automation flows data
  const flows = usePipelineStore(state => state.flows);
  const flowsLoading = usePipelineStore(state => state.flowsLoading);
  const flowsError = usePipelineStore(state => state.flowsError);

  // Selected flow data
  const selectedFlow = usePipelineStore(state => state.selectedFlow);
  const selectedFlowLoading = usePipelineStore(state => state.selectedFlowLoading);
  const selectedFlowError = usePipelineStore(state => state.selectedFlowError);

  // Pipeline operations (functions don't change, so we can get them once)
  const loadPipelines = usePipelineStore(state => state.loadPipelines);
  const loadPipelineDetails = usePipelineStore(state => state.loadPipelineDetails);
  const createPipeline = usePipelineStore(state => state.createPipeline);
  const updatePipeline = usePipelineStore(state => state.updatePipeline);
  const deletePipeline = usePipelineStore(state => state.deletePipeline);
  const togglePipelineStatus = usePipelineStore(state => state.togglePipelineStatus);

  // Stage operations
  const loadStages = usePipelineStore(state => state.loadStages);
  const createStage = usePipelineStore(state => state.createStage);
  const updateStage = usePipelineStore(state => state.updateStage);
  const deleteStage = usePipelineStore(state => state.deleteStage);
  const reorderStages = usePipelineStore(state => state.reorderStages);

  // Rule operations
  const loadRules = usePipelineStore(state => state.loadRules);
  const createRule = usePipelineStore(state => state.createRule);
  const updateRule = usePipelineStore(state => state.updateRule);
  const deleteRule = usePipelineStore(state => state.deleteRule);
  const toggleRuleStatus = usePipelineStore(state => state.toggleRuleStatus);

  // Kanban/Item operations
  const loadPipelineBoard = usePipelineStore(state => state.loadPipelineBoard);
  const addCustomerToPipeline = usePipelineStore(state => state.addCustomerToPipeline);
  const moveCustomerToStage = usePipelineStore(state => state.moveCustomerToStage);
  const removeCustomerFromPipeline = usePipelineStore(state => state.removeCustomerFromPipeline);
  const loadStageItems = usePipelineStore(state => state.loadStageItems);
  const loadCustomerPipelineStatus = usePipelineStore(state => state.loadCustomerPipelineStatus);

  // Flow operations
  const loadFlows = usePipelineStore(state => state.loadFlows);
  const loadFlowDetails = usePipelineStore(state => state.loadFlowDetails);
  const createFlow = usePipelineStore(state => state.createFlow);
  const updateFlow = usePipelineStore(state => state.updateFlow);
  const deleteFlow = usePipelineStore(state => state.deleteFlow);
  const saveFlowConfig = usePipelineStore(state => state.saveFlowConfig);

  // Utility functions
  const clearPipelinesError = usePipelineStore(state => state.clearPipelinesError);
  const clearSelectedPipeline = usePipelineStore(state => state.clearSelectedPipeline);
  const refreshPipelines = usePipelineStore(state => state.refreshPipelines);
  const getPipelineById = usePipelineStore(state => state.getPipelineById);
  const getStageById = usePipelineStore(state => state.getStageById);
  const getRuleById = usePipelineStore(state => state.getRuleById);

  // Connection state
  const isConnected = usePipelineStore(state => state.isConnected);

  // Internal initialization state (for fallback check)
  const _initialized = usePipelineStore(state => state._initialized);

  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ usePipelines: Store not initialized at app level, trying fallback');
    socket.initializeStore('PipelineStore');
  }

  // Return the complete interface
  return {
    // Main pipelines data
    pipelines,
    pipelinesLoading,
    pipelinesError,

    // Selected pipeline data
    selectedPipeline,
    selectedPipelineLoading,
    selectedPipelineError,

    // Stages data
    stages,
    stagesLoading,
    stagesError,

    // Rules data
    rules,
    rulesLoading,
    rulesError,

    // Kanban board data
    pipelineBoard,
    boardLoading,
    boardError,

    // Automation flows data
    flows,
    flowsLoading,
    flowsError,

    // Selected flow data
    selectedFlow,
    selectedFlowLoading,
    selectedFlowError,

    // Pipeline operations
    loadPipelines,
    loadPipelineDetails,
    createPipeline,
    updatePipeline,
    deletePipeline,
    togglePipelineStatus,

    // Stage operations
    loadStages,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,

    // Rule operations
    loadRules,
    createRule,
    updateRule,
    deleteRule,
    toggleRuleStatus,

    // Kanban/Item operations
    loadPipelineBoard,
    addCustomerToPipeline,
    moveCustomerToStage,
    removeCustomerFromPipeline,
    loadStageItems,
    loadCustomerPipelineStatus,

    // Flow operations
    loadFlows,
    loadFlowDetails,
    createFlow,
    updateFlow,
    deleteFlow,
    saveFlowConfig,

    // Utility functions
    clearPipelinesError,
    clearSelectedPipeline,
    refreshPipelines,
    getPipelineById,
    getStageById,
    getRuleById,

    // Connection state
    isConnected
  };
};

export default usePipelines;
